import axios from 'axios';
import { AssetPrice, HistoricalData } from './api';

/**
 * Financial Modeling Prep API服务
 * 需要API密钥
 * 专门支持指数数据
 */
class FinancialModelingPrepService {
  private client = axios.create({
    baseURL: 'https://financialmodelingprep.com',
    timeout: 10000,
  });

  // API密钥（优先级：用户配置 > 环境变量）
  private apiKey: string | null = null;

  constructor() {
    // 优先级 1: 优先从 settingsStore（用户配置）读取
    try {
      const { useSettingsStore } = require('@/stores/settingsStore');
      const apiKeys = useSettingsStore.getState().apiKeys;
      if (apiKeys?.financialmodelingprep && apiKeys.financialmodelingprep.trim() !== '') {
        this.apiKey = apiKeys.financialmodelingprep;
        return; // 如果找到用户配置，直接返回，不再检查环境变量
      }
    } catch (e) {
      // 忽略错误，继续检查环境变量
    }
    
    // 优先级 2: 如果没有用户配置，尝试从环境变量读取（向后兼容）
    this.apiKey = (import.meta as any)?.env?.VITE_FINANCIALMODELINGPREP_API_KEY || null;
  }

  setApiKey(apiKey: string | null): void {
    this.apiKey = apiKey;
  }

  /**
   * 转换指数符号为 FMP 格式
   * FMP 使用带 ^ 前缀的符号格式
   */
  private convertToFmpSymbol(symbol: string): string {
    const upperSymbol = symbol.toUpperCase();
    
    // 符号映射表
    const symbolMap: Record<string, string> = {
      'SPX': '^GSPC',
      'S&P500': '^GSPC',
      'S&P 500': '^GSPC',
      'S&p500': '^GSPC',
      's&p500': '^GSPC',
      's&p 500': '^GSPC',
      'IXIC': '^IXIC',
      'NASDAQ': '^IXIC',
      'NASDAQ Composite': '^IXIC',
      'nasdaq': '^IXIC',
      'nasdaq composite': '^IXIC',
      'DJIA': '^DJI',
      'DOW': '^DJI',
      'Dow Jones': '^DJI',
      'dow jones': '^DJI',
      'DJI': '^DJI',
    };

    // 如果已经有 ^ 前缀，直接返回
    if (upperSymbol.startsWith('^')) {
      return upperSymbol;
    }

    // 查找映射
    const mapped = symbolMap[upperSymbol] || symbolMap[symbol.toLowerCase()];
    if (mapped) {
      return mapped;
    }

    // 如果没有映射，尝试添加 ^ 前缀
    return `^${upperSymbol}`;
  }

  /**
   * 获取指数价格
   */
  async getIndexPrice(symbol: string): Promise<AssetPrice> {
    if (!this.apiKey) throw new Error('Financial Modeling Prep API key is required');
    
    const fmpSymbol = this.convertToFmpSymbol(symbol);
    
    const response = await this.client.get('/stable/quote', {
      params: {
        symbol: fmpSymbol,
        apikey: this.apiKey,
      },
    });

    const data = response.data;
    
    // FMP 返回数组格式
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error(`Index not found: ${symbol}`);
    }

    const quote = data[0];
    if (!quote || quote.price === undefined || quote.price === null) {
      throw new Error(`Invalid index data: ${symbol}`);
    }

    const currentPrice = parseFloat(String(quote.price)) || 0;
    const priceChange = parseFloat(String(quote.change || 0)) || 0;
    // API 返回的字段名是 changePercentage，不是 changesPercentage
    const priceChangePercent = parseFloat(String(quote.changePercentage || 0)) || 0;

    // 使用API返回的实际symbol（可能带^前缀），但移除^前缀用于匹配
    const apiSymbol = quote.symbol || fmpSymbol;
    const normalizedSymbol = apiSymbol.replace(/^\^/, '').toUpperCase();

    return {
      id: symbol.toLowerCase(),
      symbol: normalizedSymbol, // 使用规范化后的symbol（移除^前缀），确保能正确匹配
      name: quote.name || normalizedSymbol,
      current_price: currentPrice,
      price_change_24h: priceChange,
      price_change_percentage_24h: priceChangePercent,
      last_updated: quote.timestamp ? new Date(quote.timestamp * 1000).toISOString() : new Date().toISOString(),
      source_url: 'https://financialmodelingprep.com',
    };
  }

  /**
   * 批量获取指数价格
   * 使用 /stable/quote 端点，对每个指数单独请求
   */
  async getBatchIndexQuotes(symbols: string[]): Promise<AssetPrice[]> {
    if (!this.apiKey) throw new Error('Financial Modeling Prep API key is required');
    
    if (symbols.length === 0) {
      return [];
    }
    
    const concurrency = 5;
    const results: AssetPrice[] = [];
    const errors: Array<{ symbol: string; error: Error }> = [];
    const queue = [...symbols];

    const workers = Array.from({ length: concurrency }, async () => {
      while (queue.length > 0) {
        const symbol = queue.shift();
        if (!symbol) break;
        try {
          const price = await this.getIndexPrice(symbol);
          results.push(price);
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          errors.push({ symbol, error: err });
          console.warn(`[FMP] Failed to get price for index ${symbol}:`, err.message);
        }
      }
    });

    await Promise.all(workers);
    
    // 如果所有请求都失败，抛出错误
    if (results.length === 0 && errors.length > 0) {
      const errorMessages = errors.map(e => `${e.symbol}: ${e.error.message}`).join('; ');
      throw new Error(`All index price requests failed: ${errorMessages}`);
    }
    
    // 如果部分失败，记录警告但返回成功的结果
    if (errors.length > 0) {
      console.warn(`[FMP] Some index prices failed: ${errors.length} out of ${symbols.length} requests failed`);
    }
    
    return results;
  }

  /**
   * 获取指数历史数据
   */
  async getIndexHistorical(symbol: string, days: number = 7): Promise<HistoricalData> {
    if (!this.apiKey) throw new Error('Financial Modeling Prep API key is required');
    
    const fmpSymbol = this.convertToFmpSymbol(symbol);
    
    // 计算日期范围
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - days);
    
    const response = await this.client.get(`/api/v3/historical-price-full/index/${encodeURIComponent(fmpSymbol)}`, {
      params: {
        from: fromDate.toISOString().split('T')[0],
        to: toDate.toISOString().split('T')[0],
        apikey: this.apiKey,
      },
    });

    const data = response.data;
    
    if (!data || !data.historical || !Array.isArray(data.historical)) {
      throw new Error(`Historical data not found for index: ${symbol}`);
    }

    const historical = data.historical.reverse(); // 确保时间顺序正确

    const prices: [number, number][] = historical.map((item: any) => [
      new Date(item.date).getTime(),
      parseFloat(item.close) || 0,
    ]);

    // 计算市场总值和交易量（使用价格作为近似值）
    const market_caps: [number, number][] = prices.map(([time, price]) => [time, price * 1000000]);
    const total_volumes: [number, number][] = historical.map((item: any) => [
      new Date(item.date).getTime(),
      parseFloat(item.volume) || 0,
    ]);

    return {
      prices,
      market_caps,
      total_volumes,
    };
  }

  /**
   * 获取可用指数列表
   */
  async getAvailableIndexes(): Promise<Array<{ symbol: string; name: string; exchange?: string }>> {
    if (!this.apiKey) throw new Error('Financial Modeling Prep API key is required');
    
    const response = await this.client.get('/api/v3/symbol/available-indexes', {
      params: {
        apikey: this.apiKey,
      },
    });

    const data = response.data;
    
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map((item: any) => ({
      symbol: item.symbol || '',
      name: item.name || '',
      exchange: item.exchange || undefined,
    }));
  }
}

export const financialModelingPrepService = new FinancialModelingPrepService();
